import React, { useState } from 'react';
import { Heart, Send } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  initialSubmissionData,
  supplierFields,
  buildSubmissionContent,
} from '../data/submissionForm';
import CollapsibleSection from '../components/submission/CollapsibleSection';
import SubmissionHero from '../components/submission/SubmissionHero';
import SubmissionSuccess from '../components/submission/SubmissionSuccess';
import PhotoInstructions from '../components/submission/PhotoInstructions';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WeddingSubmissionPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    basics: true,
    loveStory: false,
    ceremony: false,
    celebration: false,
    special: false,
    suppliers: false,
  });
  const [formData, setFormData] = useState(initialSubmissionData);

  const toggleSection = (section) =>
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contacts`, {
        name: `${formData.name} & ${formData.partnerName}`,
        email: formData.email,
        message: `[Wedding Story Submission] ${buildSubmissionContent(formData)}`,
      });
      setSubmitted(true);
    } catch (error) {
      alert('There was an error submitting your story. Please try again or email us directly.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) return <SubmissionSuccess />;

  // Helper for labelled textareas with hints
  const TA = ({ name, label, hint, rows = 3, placeholder }) => (
    <div>
      <label className="block text-sm text-[#6B4E37] mb-1">{label}</label>
      {hint && <p className="text-xs text-[#8B7D6B] mb-2">{hint}</p>}
      <Textarea
        name={name}
        value={formData[name]}
        onChange={handleChange}
        rows={rows}
        placeholder={placeholder}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      <SEO
        title="Share Your Wedding Story"
        description="Share your Maltese wedding story with I DO Bridal Magazine. Get featured in our next issue, it's free and takes only 5 minutes!"
      />
      <SubmissionHero />

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="wedding-submission-form">
            {/* Basic Details */}
            <CollapsibleSection
              title="Quick Details (The Basics)"
              icon={Heart}
              isOpen={expandedSections.basics}
              onToggle={() => toggleSection('basics')}
            >
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#6B4E37] mb-1">Your Name *</label>
                  <Input name="name" value={formData.name} onChange={handleChange} required placeholder="Your full name" />
                </div>
                <div>
                  <label className="block text-sm text-[#6B4E37] mb-1">Your Age</label>
                  <Input name="age" value={formData.age} onChange={handleChange} placeholder="Age" />
                </div>
              </div>
              <div>
                <label className="block text-sm text-[#6B4E37] mb-1">What do you do for work (or love!)?</label>
                <Input name="occupation" value={formData.occupation} onChange={handleChange} placeholder="Occupation or passion" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#6B4E37] mb-1">Partner's Name *</label>
                  <Input name="partnerName" value={formData.partnerName} onChange={handleChange} required placeholder="Partner's full name" />
                </div>
                <div>
                  <label className="block text-sm text-[#6B4E37] mb-1">Partner's Age</label>
                  <Input name="partnerAge" value={formData.partnerAge} onChange={handleChange} placeholder="Age" />
                </div>
              </div>
              <div>
                <label className="block text-sm text-[#6B4E37] mb-1">Partner's Occupation</label>
                <Input name="partnerOccupation" value={formData.partnerOccupation} onChange={handleChange} placeholder="What they do" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#6B4E37] mb-1">Contact Email *</label>
                  <Input name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="your@email.com" />
                </div>
                <div>
                  <label className="block text-sm text-[#6B4E37] mb-1">Phone Number</label>
                  <Input name="phone" value={formData.phone} onChange={handleChange} placeholder="+356..." />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#6B4E37] mb-1">Wedding Date *</label>
                  <Input name="weddingDate" type="date" value={formData.weddingDate} onChange={handleChange} required />
                </div>
                <div>
                  <label className="block text-sm text-[#6B4E37] mb-1">Wedding Location *</label>
                  <Input name="weddingLocation" value={formData.weddingLocation} onChange={handleChange} required placeholder="Venue & location" />
                </div>
              </div>
            </CollapsibleSection>

            {/* Love Story */}
            <CollapsibleSection
              title="Your Love Story"
              icon={Heart}
              isOpen={expandedSections.loveStory}
              onToggle={() => toggleSection('loveStory')}
            >
              <TA name="howMet" label="How did this all begin?" hint="Where did you meet, who made the first move, and when did you know?" rows={4} placeholder="Tell us your story..." />
              <TA name="whyNow" label="Why now?" hint="What inspired you to get married when you did?" placeholder="The timing felt right because..." />
              <TA name="themeVibe" label="Theme, colour palette, or vibe?" hint="Tell us all about the look and feel you were going for." placeholder="Our wedding aesthetic was..." />
              <TA name="invitationIdeas" label="Keepsakes or creative invitation ideas?" hint="Wax seals, pet cameos, personalised tags, we love them all!" placeholder="Our special touches included..." />
            </CollapsibleSection>

            {/* Ceremony */}
            <CollapsibleSection
              title="The Ceremony"
              icon={Heart}
              isOpen={expandedSections.ceremony}
              onToggle={() => toggleSection('ceremony')}
            >
              <TA name="ceremonySpot" label="How did you choose your ceremony spot?" hint="Church? Cliffside? Hidden garden?" placeholder="We chose this spot because..." />
              <TA name="fashionMoment" label="This is the fashion moment!" hint="Tell us about the dress, suit, jumpsuit or something unusual! What made you say yes to the look?" rows={4} placeholder="The outfit story..." />
              <TA name="bouquet" label="Bouquet talk" hint="What flowers did you carry and why did you choose them?" placeholder="My bouquet featured..." />
              <TA name="rings" label="Let's talk rings" hint="Was there a proposal story? Engraving? A unique twist or family heirloom?" placeholder="Our ring story..." />
              <TA name="ceremonySpecial" label="What made your ceremony feel true to you?" hint="Did you write your vows, walk down the aisle to a special song, or include your dog as a ring bearer?" rows={4} placeholder="The special touches that made it ours..." />
            </CollapsibleSection>

            {/* Celebration */}
            <CollapsibleSection
              title="The Celebration"
              icon={Heart}
              isOpen={expandedSections.celebration}
              onToggle={() => toggleSection('celebration')}
            >
              <TA name="venueDetails" label="Where did the party happen?" hint="Why did you pick this venue, and was there dancing on tables?" placeholder="We celebrated at..." />
              <TA name="decorDreams" label="Decor dreams" hint="Tell us about your colour scheme, table settings, flowers, and any DIY magic." rows={4} placeholder="Our decor vision..." />
              <TA name="menuDetails" label="What was on the menu?" hint="From fine dining to food trucks, tell us what guests were munching and sipping." placeholder="We served..." />
              <TA name="entertainment" label="Entertainment time!" hint="Live band? Surprise dance? Flash mob? Give us the scoop." placeholder="The entertainment included..." />
              <TA name="firstDance" label="First dance feels" hint="What song did you dance to, and was it romantic, hilarious or both?" placeholder="Our first dance..." />
              <TA name="dreamTeam" label="Your behind-the-scenes dream team" hint="Shout out the friends, family, and suppliers who helped bring it all together." rows={4} placeholder="Special thanks to..." />
            </CollapsibleSection>

            {/* Special Moments */}
            <CollapsibleSection
              title="Extra Special Moments"
              icon={Heart}
              isOpen={expandedSections.special}
              onToggle={() => toggleSection('special')}
            >
              <p className="text-[#6B4E37] italic">We love the little things that made your day unforgettable!</p>
              <TA name="petAppearance" label="Did your pet make an appearance?" rows={2} placeholder="Tell us everything about your furry friend's role!" />
              <TA name="mishapsSurprises" label="Any hilarious mishaps or emotional surprises?" placeholder="The unexpected moments..." />
              <TA name="unconventionalChoices" label="Was your theme, dress or venue a bit unconventional?" hint="We love bold choices!" placeholder="We did things differently by..." />
              <TA name="aboveAndBeyond" label="Any moments where guests or vendors really went above and beyond?" placeholder="They surprised us when..." />
            </CollapsibleSection>

            {/* Suppliers */}
            <CollapsibleSection
              title="Your Supplier List"
              icon={Heart}
              isOpen={expandedSections.suppliers}
              onToggle={() => toggleSection('suppliers')}
            >
              <p className="text-[#6B4E37] mb-4">
                Please list the fab team who helped you make it happen. Include names, websites, and social handles if you've got them.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {supplierFields.map((f) => (
                  <div key={f.name}>
                    <label className="block text-sm text-[#6B4E37] mb-1">{f.label}</label>
                    <Input
                      name={f.name}
                      value={formData[f.name]}
                      onChange={handleChange}
                      placeholder="Name & details"
                    />
                  </div>
                ))}
              </div>
            </CollapsibleSection>

            {/* Additional Info */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-[#E5E0DA]">
              <label
                className="block text-lg font-medium text-[#2C2C2C] mb-2"
                style={{ fontFamily: '"Playfair Display", serif' }}
              >
                Anything else you'd love to share?
              </label>
              <Textarea
                name="additionalInfo"
                value={formData.additionalInfo}
                onChange={handleChange}
                rows={4}
                placeholder="Pop it here or attach anything additional!"
              />
            </div>

            <PhotoInstructions />

            {/* Submit */}
            <div className="text-center pt-6">
              <Button
                type="submit"
                disabled={loading}
                className="bg-[#DE92A9] hover:bg-[#C9758F] text-white px-12 py-6 text-lg tracking-wider"
                data-testid="submit-wedding-story-btn"
              >
                {loading ? 'Submitting...' : 'Submit Your Story'}
                <Send className="ml-2" size={20} />
              </Button>
              <p className="text-[#8B7D6B] text-sm mt-4">We're here to make your story shine!</p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default WeddingSubmissionPage;
